import { Fragment, useEffect, useMemo, useRef, useState } from "react";
import Layout from "@/components/Layout";
import api, { apiErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import QAVideoManager from "@/pages/tuition/QAVideoManager";
import { Button } from "@/components/ui/button";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import { Progress } from "@/components/ui/progress";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Library, CheckCircle2, CircleDashed, FileText, Upload, Loader2, RefreshCw, FileSearch, Pencil, FileDown, AlertTriangle, FileUp, Ban, Trash2 } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";

const fmtBytes = (n) => {
  if (!n && n !== 0) return "—";
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
};
const fmtWhen = (iso) => {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleString(); } catch { return iso; }
};

const jobPct = (job) => {
  if (!job) return 0;
  switch (job.stage) {
    case "queued": return 5;
    case "extracting": return 12;
    case "embedding": {
      const t = job.chunks_total || 0, d = job.chunks_done || 0;
      return t ? Math.min(90, 15 + Math.round((d / t) * 70)) : 20;
    }
    case "storing": return 95;
    case "done": return 100;
    default: return 8;
  }
};
const jobLabel = (job) => {
  switch (job?.stage) {
    case "queued": return "Queued…";
    case "extracting": return "Reading PDF…";
    case "embedding": return `Indexing ${job.chunks_done || 0}/${job.chunks_total || "…"}`;
    case "storing": return "Saving…";
    default: return "Indexing…";
  }
};

export default function AdminTuitionLibrary({ embedded } = {}) {
  const { user } = useAuth();
  const isDevAdmin = !!user?.is_dev_admin;
  const [data, setData] = useState({ subjects: [], totals: { indexed: 0, lessons: 0 } });
  const [loading, setLoading] = useState(true);
  const [pendingLesson, setPendingLesson] = useState(null); // lesson obj awaiting upload
  const [jobsByLesson, setJobsByLesson] = useState({}); // lessonId -> job {job_id,status,stage,chunks_done,chunks_total}
  const [lessonDialog, setLessonDialog] = useState(null); // null = closed, {lesson} = edit
  const [lessonTitle, setLessonTitle] = useState("");
  const [lessonOrder, setLessonOrder] = useState("");
  const [lessonVideo, setLessonVideo] = useState("");
  const [savingLesson, setSavingLesson] = useState(false);
  const [videoInfo, setVideoInfo] = useState(null); // {video_file_id, video_filename, video_size} of the open lesson
  const [videoUploadPct, setVideoUploadPct] = useState(null); // null = idle, 0..100 = uploading
  const [removingVideo, setRemovingVideo] = useState(false);
  const [audioInfo, setAudioInfo] = useState(null);        // {audio_file_id, audio_filename, audio_size}
  const [audioUploadPct, setAudioUploadPct] = useState(null);
  const [removingAudio, setRemovingAudio] = useState(false);
  const audioInputRef = useRef(null);
  // ── MBA (Medium-Bank Assessment) PDF — Dev-Admin only ─────────────────
  // Uploaded here in Tuition Library, consumed as the STRICT source for
  // MEDIUM-level question papers (mirrors the LBA→Easy contract).
  const [mbaInfo, setMbaInfo] = useState(null);       // {mba_file_id, mba_filename, mba_size, mba_uploaded_at}
  const [mbaBusy, setMbaBusy] = useState(false);
  const mbaInputRef = useRef(null);
  const [reprocessing, setReprocessing] = useState(false);
  const [healthLoading, setHealthLoading] = useState(false);
  const [healthResult, setHealthResult] = useState(null);
  const [healthMap, setHealthMap] = useState({});
  const videoInputRef = useRef(null);
  const [classOptions, setClassOptions] = useState({ classes: ["5"], all_classes: ["5"] });
  const [classFilter, setClassFilter] = useState("5");
  const [subjectFilter, setSubjectFilter] = useState("");
  const fileInputRef = useRef(null);
  const csvInputRef = useRef(null);
  const [importingLessons, setImportingLessons] = useState(false);
  const [lessonImportErrors, setLessonImportErrors] = useState([]);
  const [autoRetry, setAutoRetry] = useState(false);

  const load = async (cn = classFilter) => {
    setLoading(true);
    try {
      const { data } = await api.get("/tuition/library", { params: { class_name: cn } });
      setData(data);
      // Resume any in-flight indexing jobs so progress survives refresh / re-open.
      const resumed = {};
      (data.subjects || []).forEach((s) => (s.lessons || []).forEach((l) => {
        if (l.job && l.job.status === "processing") resumed[l.id] = l.job;
      }));
      if (Object.keys(resumed).length) setJobsByLesson((prev) => ({ ...prev, ...resumed }));
    } catch (e) {
      toast.error(apiErr(e.response?.data?.detail) || "Could not load library");
    } finally { setLoading(false); }
  };
  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/tuition/library/classes");
        setClassOptions({
          classes: data.classes?.length ? data.classes : ["5"],
          all_classes: data.all_classes || ["5"],
        });
      } catch { /* fall back to defaults */ }
      await load("5");
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  useEffect(() => { if (classFilter) load(classFilter); /* eslint-disable-next-line */ }, [classFilter]);

  // Load the auto-retry toggle state once.
  useEffect(() => {
    (async () => {
      try { const { data } = await api.get("/tuition/library/autoretry"); setAutoRetry(!!data.enabled); } catch { /* ignore */ }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const toggleAutoRetry = async (v) => {
    setAutoRetry(v);
    try {
      await api.put("/tuition/library/autoretry", { enabled: v });
      toast.success(v ? "Auto-retry on — failed PDFs re-index automatically." : "Auto-retry off.");
    } catch (err) {
      setAutoRetry(!v);
      toast.error(apiErr(err.response?.data?.detail) || "Could not update auto-retry");
    }
  };

  // Default the subject filter to the first subject whenever the loaded subject
  // list changes (class switch / refresh). Keeps the picker stable as long as
  // the previously-selected subject still exists in the new class.
  useEffect(() => {
    if (!data.subjects?.length) { setSubjectFilter(""); return; }
    if (!subjectFilter || !data.subjects.find((s) => s.id === subjectFilter)) {
      setSubjectFilter(data.subjects[0].id);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data.subjects]);

  const activeSubject = useMemo(
    () => data.subjects?.find((s) => s.id === subjectFilter) || null,
    [data.subjects, subjectFilter],
  );

  const totals = activeSubject
    ? { indexed: activeSubject.indexed_count, lessons: activeSubject.total_count }
    : (data.totals || { indexed: 0, lessons: 0 });
  const pct = totals.lessons ? Math.round((totals.indexed / totals.lessons) * 100) : 0;

  const removePdf = async (lesson) => {
    try {
      await api.delete(`/tuition/library/${lesson.id}`);
      toast.success("PDF removed. You can upload a fresh one anytime.");
      load();
    } catch (err) {
      toast.error(apiErr(err.response?.data?.detail) || "Could not remove the PDF");
    }
  };

  const cancelJob = async (lesson) => {
    const job = jobsByLesson[lesson.id];
    if (!job?.job_id) {
      // Upload still in flight — just clear the local indicator.
      setJobsByLesson((prev) => { const n = { ...prev }; delete n[lesson.id]; return n; });
      return;
    }
    try {
      await api.post(`/tuition/library/jobs/${job.job_id}/cancel`);
      setJobsByLesson((prev) => { const n = { ...prev }; delete n[lesson.id]; return n; });
      toast.success("Indexing stopped. You can re-upload this PDF whenever you're ready.");
      load();
    } catch (err) {
      toast.error(apiErr(err.response?.data?.detail) || "Could not stop indexing");
      load();
    }
  };

  const openPicker = (lesson) => {
    setPendingLesson(lesson);
    // Reset value so re-picking the same file still fires onChange
    if (fileInputRef.current) fileInputRef.current.value = "";
    fileInputRef.current?.click();
  };

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    const lesson = pendingLesson;
    setPendingLesson(null);
    if (!file || !lesson) return;
    if (file.type && file.type !== "application/pdf") {
      toast.error("Please pick a PDF file.");
      return;
    }
    // Upload in chunks so large (e.g. OCR-converted) PDFs bypass the proxy's
    // request body-size limit — the same mechanism the video upload uses.
    setJobsByLesson((prev) => ({ ...prev, [lesson.id]: { status: "processing", stage: "queued", chunks_done: 0, chunks_total: 0 } }));
    try {
      const { data: initData } = await api.post(`/tuition/library/${lesson.id}/pdf/init`, {
        filename: file.name,
        size: file.size,
      });
      const uploadId = initData.upload_id;
      const CHUNK = 5 * 1024 * 1024; // 5 MB per request — safely under any ingress cap
      let offset = 0;
      while (offset < file.size) {
        const slice = file.slice(offset, offset + CHUNK);
        const buf = await slice.arrayBuffer();
        await api.put(`/tuition/library/pdf-upload/${uploadId}`, buf, {
          headers: { "Content-Type": "application/octet-stream" },
        });
        offset += CHUNK;
      }
      const { data: res } = await api.post(`/tuition/library/pdf-upload/${uploadId}/complete`);
      // Server now indexes in the background — track the job id for polling.
      setJobsByLesson((prev) => ({
        ...prev,
        [lesson.id]: { job_id: res.job_id, status: "processing", stage: "queued", chunks_done: 0, chunks_total: 0 },
      }));
      toast.success("Upload received — indexing in the background. You can keep working.");
    } catch (err) {
      const detail = apiErr(err.response?.data?.detail) || "Upload failed";
      toast.error(detail);
      setJobsByLesson((prev) => { const n = { ...prev }; delete n[lesson.id]; return n; });
    }
  };

  // Poll every in-flight indexing job; refresh + toast on completion/failure.
  const activeJobKey = useMemo(
    () => Object.values(jobsByLesson).filter((j) => j?.status === "processing" && j?.job_id).map((j) => j.job_id).sort().join(","),
    [jobsByLesson],
  );
  useEffect(() => {
    if (!activeJobKey) return;
    const iv = setInterval(async () => {
      const ids = activeJobKey.split(",").filter(Boolean);
      for (const jobId of ids) {
        try {
          const { data: job } = await api.get(`/tuition/library/jobs/${jobId}`);
          if (job.status === "done") {
            toast.success(`Indexed ${job.chunk_count} chunk${job.chunk_count === 1 ? "" : "s"} from ${job.page_count} page(s).`);
            setJobsByLesson((prev) => { const n = { ...prev }; delete n[job.lesson_id]; return n; });
            load();
          } else if (job.status === "failed") {
            toast.error(job.error || "Indexing failed");
            setJobsByLesson((prev) => { const n = { ...prev }; delete n[job.lesson_id]; return n; });
            load();
          } else {
            setJobsByLesson((prev) => ({ ...prev, [job.lesson_id]: job }));
          }
        } catch { /* transient — retry next tick */ }
      }
    }, 1800);
    return () => clearInterval(iv);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeJobKey]);

  // ── CSV lesson bulk-import ─────────────────────────────────────────────
  const downloadLessonTemplate = async () => {
    try {
      const res = await api.get("/tuition/library/lessons/template", { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url; a.download = "tuition_lessons_template.csv";
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
    } catch { toast.error("Could not download template"); }
  };

  const downloadLessonImportErrors = () => {
    if (!lessonImportErrors.length) return;
    const esc = (v) => `"${String(v ?? "").replace(/"/g, '""')}"`;
    const csv = ["row,error", ...lessonImportErrors.map((r) => `${esc(r.row)},${esc(r.error)}`)].join("\n");
    const url = window.URL.createObjectURL(new Blob([csv], { type: "text/csv" }));
    const a = document.createElement("a");
    a.href = url; a.download = "lesson_import_errors.csv";
    document.body.appendChild(a); a.click(); a.remove();
    window.URL.revokeObjectURL(url);
  };

  const onLessonCsv = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setImportingLessons(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data: res } = await api.post("/tuition/library/lessons/import", fd, {
        params: { class_name: classFilter },
        headers: { "Content-Type": "multipart/form-data" },
      });
      const parts = [`${res.created} added`];
      if (res.skipped_duplicates) parts.push(`${res.skipped_duplicates} duplicates skipped`);
      if (res.error_count) parts.push(`${res.error_count} rows had errors`);
      toast.success(parts.join(" · "));
      setLessonImportErrors(res.error_count ? (res.errors || []) : []);
      await load();
    } catch (err) {
      toast.error(apiErr(err.response?.data?.detail) || "Import failed");
    } finally { setImportingLessons(false); }
  };

  // ── Lesson edit / access toggle ────────────────────────────────────────
  const openEdit = (lesson) => {
    setLessonTitle(lesson.title || "");
    setLessonOrder(lesson.order != null ? String(lesson.order) : "");
    setLessonVideo(lesson.video_url || "");
    setVideoInfo(lesson.video_file_id ? {
      video_file_id: lesson.video_file_id,
      video_filename: lesson.video_filename,
      video_size: lesson.video_size,
    } : null);
    setVideoUploadPct(null);
    setAudioInfo(lesson.audio_file_id ? {
      audio_file_id: lesson.audio_file_id,
      audio_filename: lesson.audio_filename,
      audio_size: lesson.audio_size,
    } : null);
    setAudioUploadPct(null);
    setMbaInfo(lesson.mba_file_id ? {
      mba_file_id: lesson.mba_file_id,
      mba_filename: lesson.mba_filename,
      mba_size: lesson.mba_size,
      mba_uploaded_at: lesson.mba_uploaded_at,
    } : null);
    setMbaBusy(false);
    setLessonDialog({ lesson });
  };

  // Dev-Admin: upload a lesson video in chunks (bypasses ingress body limits).
  const onPickVideo = async (e) => {
    const file = e.target.files?.[0];
    if (videoInputRef.current) videoInputRef.current.value = "";
    if (!file) return;
    const okType = /^video\/(mp4|webm|ogg|quicktime|x-matroska)$/.test(file.type)
      || /\.(mp4|webm|ogv|ogg|mov|mkv)$/i.test(file.name);
    if (!okType) { toast.error("Please choose an MP4, WebM, MOV or MKV video."); return; }
    const lessonId = lessonDialog.lesson.id;
    setVideoUploadPct(0);
    try {
      const { data: initData } = await api.post(`/tuition/library/${lessonId}/video/init`, {
        filename: file.name,
        content_type: file.type || "video/mp4",
        size: file.size,
      });
      const uploadId = initData.upload_id;
      const CHUNK = 5 * 1024 * 1024; // 5 MB
      let offset = 0;
      while (offset < file.size) {
        const slice = file.slice(offset, offset + CHUNK);
        const buf = await slice.arrayBuffer();
        await api.put(`/tuition/library/video-upload/${uploadId}`, buf, {
          headers: { "Content-Type": "application/octet-stream" },
        });
        offset += CHUNK;
        setVideoUploadPct(Math.min(99, Math.round((offset / file.size) * 100)));
      }
      const { data: done } = await api.post(`/tuition/library/video-upload/${uploadId}/complete`);
      setVideoInfo({ video_file_id: done.video_file_id, video_filename: file.name, video_size: file.size });
      setVideoUploadPct(100);
      toast.success("Video uploaded. Students can now watch it in the AI Tutor.");
      setTimeout(() => setVideoUploadPct(null), 800);
      await load();
    } catch (err) {
      setVideoUploadPct(null);
      toast.error(apiErr(err.response?.data?.detail) || "Video upload failed");
    }
  };

  const removeVideo = async () => {
    const lessonId = lessonDialog.lesson.id;
    setRemovingVideo(true);
    try {
      await api.delete(`/tuition/library/${lessonId}/video`);
      setVideoInfo(null);
      toast.success("Video removed.");
      await load();
    } catch (err) {
      toast.error(apiErr(err.response?.data?.detail) || "Could not remove video");
    } finally { setRemovingVideo(false); }
  };

  // Chunked audio-podcast upload — same pattern as video, smaller cap.
  const onPickAudio = async (e) => {
    const file = e.target.files?.[0];
    if (audioInputRef.current) audioInputRef.current.value = "";
    if (!file) return;
    const okType = /^audio\/(mpeg|mp3|mp4|aac|x-m4a|wav|x-wav|ogg|webm)$/.test(file.type)
      || /\.(mp3|m4a|aac|wav|ogg|webm)$/i.test(file.name);
    if (!okType) { toast.error("Please choose an MP3, M4A, AAC, WAV or OGG audio file."); return; }
    const lessonId = lessonDialog.lesson.id;
    setAudioUploadPct(0);
    try {
      const { data: initData } = await api.post(`/tuition/library/${lessonId}/audio/init`, {
        filename: file.name,
        content_type: file.type || "audio/mpeg",
        size: file.size,
      });
      const uploadId = initData.upload_id;
      const CHUNK = 5 * 1024 * 1024;
      let offset = 0;
      while (offset < file.size) {
        const slice = file.slice(offset, offset + CHUNK);
        const buf = await slice.arrayBuffer();
        await api.put(`/tuition/library/audio-upload/${uploadId}`, buf, {
          headers: { "Content-Type": "application/octet-stream" },
        });
        offset += CHUNK;
        setAudioUploadPct(Math.min(99, Math.round((offset / file.size) * 100)));
      }
      const { data: done } = await api.post(`/tuition/library/audio-upload/${uploadId}/complete`);
      setAudioInfo({ audio_file_id: done.audio_file_id, audio_filename: file.name, audio_size: file.size });
      setAudioUploadPct(100);
      toast.success("Audio podcast uploaded. Students can now listen in the AI Tutor.");
      setTimeout(() => setAudioUploadPct(null), 800);
      await load();
    } catch (err) {
      setAudioUploadPct(null);
      toast.error(apiErr(err.response?.data?.detail) || "Audio upload failed");
    }
  };

  const removeAudio = async () => {
    const lessonId = lessonDialog.lesson.id;
    setRemovingAudio(true);
    try {
      await api.delete(`/tuition/library/${lessonId}/audio`);
      setAudioInfo(null);
      toast.success("Audio podcast removed.");
      await load();
    } catch (err) {
      toast.error(apiErr(err.response?.data?.detail) || "Could not remove audio");
    } finally { setRemovingAudio(false); }
  };

  // ── MBA (Medium-Bank Assessment) PDF handlers — Dev Admin only ────────
  const onPickMba = async (e) => {
    const file = e.target.files?.[0];
    if (mbaInputRef.current) mbaInputRef.current.value = "";
    if (!file) return;
    if (file.type !== "application/pdf" && !/\.pdf$/i.test(file.name)) {
      toast.error("Please choose a PDF file.");
      return;
    }
    if (file.size > 25 * 1024 * 1024) {
      toast.error("PDF too large (max 25 MB).");
      return;
    }
    setMbaBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data: done } = await api.post(`/tuition/library/lessons/${lessonDialog.lesson.id}/mba`, fd);
      setMbaInfo({
        mba_file_id: "1", mba_filename: done.mba_filename,
        mba_size: done.mba_size, mba_uploaded_at: done.mba_uploaded_at,
      });
      toast.success("Medium-bank PDF uploaded.");
      await load();
    } catch (err) {
      toast.error(apiErr(err.response?.data?.detail) || "Could not upload Medium-bank PDF");
    } finally { setMbaBusy(false); }
  };

  const removeMba = async () => {
    if (!lessonDialog?.lesson?.id) return;
    setMbaBusy(true);
    try {
      await api.delete(`/tuition/library/lessons/${lessonDialog.lesson.id}/mba`);
      setMbaInfo(null);
      toast.success("Medium-bank PDF removed.");
      await load();
    } catch (err) {
      toast.error(apiErr(err.response?.data?.detail) || "Could not remove Medium-bank PDF");
    } finally { setMbaBusy(false); }
  };

  const previewMba = async () => {
    try {
      const res = await api.get(`/tuition/library/lessons/${lessonDialog.lesson.id}/mba`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      window.open(url, "_blank");
      setTimeout(() => window.URL.revokeObjectURL(url), 5000);
    } catch (err) {
      toast.error(apiErr(err.response?.data?.detail) || "Could not open Medium-bank PDF");
    }
  };

  // Dev-Admin one-time maintenance: relocate moov atom to the front for existing
  // MP4/MOV videos uploaded before faststart processing existed.
  const checkTextHealth = async () => {
    setHealthLoading(true);
    try {
      const { data } = await api.get("/tuition/library/lesson-health");
      setHealthResult(data);
      setHealthMap(data.by_lesson || {});
      if ((data.flagged_count || 0) === 0) {
        toast.success(data.checked ? `All ${data.checked} language-subject lesson(s) have clean text.` : "No language-subject lessons with indexed PDFs found.");
      }
    } catch (err) {
      toast.error(apiErr(err.response?.data?.detail) || "Health check failed");
    } finally {
      setHealthLoading(false);
    }
  };

  // Silently load text-health once so garbled lessons get a badge in the list.
  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/tuition/library/lesson-health");
        setHealthMap(data.by_lesson || {});
      } catch { /* non-blocking */ }
    })();
  }, []);

  const reprocessVideos = async () => {
    setReprocessing(true);
    toast.message("Re-processing videos…", { description: "Optimising & converting videos to a browser-friendly format (H.264). This may take a minute." });
    try {
      const { data } = await api.post(`/tuition/library/videos/reprocess-faststart`);
      const parts = [`${data.processed} fixed`, `${data.already_ok} already OK`];
      if (data.failed) parts.push(`${data.failed} failed`);
      const corrupt = data.corrupt || [];
      if (corrupt.length) {
        toast.error(
          `${corrupt.length} video(s) are corrupted and can't be repaired — please DELETE and RE-UPLOAD them: ` +
          corrupt.map((c) => c.filename || c.lesson_id).join(", "),
          { duration: 12000 },
        );
      } else {
        toast.success(`Done — ${parts.join(", ")} (of ${data.total} videos).`);
      }
    } catch (err) {
      toast.error(apiErr(err.response?.data?.detail) || "Could not re-process videos");
    } finally { setReprocessing(false); }
  };
  const saveLesson = async () => {
    const title = (lessonTitle || "").trim();
    if (!title) { toast.error("Lesson title is required."); return; }
    const order = lessonOrder === "" ? undefined : Number(lessonOrder);
    if (order !== undefined && (!Number.isFinite(order) || order < 0)) {
      toast.error("Order must be a non-negative number.");
      return;
    }
    const video_url = (lessonVideo || "").trim();
    if (video_url && !/^https?:\/\//i.test(video_url)) {
      toast.error("Video URL must start with http:// or https://");
      return;
    }
    setSavingLesson(true);
    try {
      await api.patch(`/tuition/library/lessons/${lessonDialog.lesson.id}`, { title, order, video_url });
      toast.success("Lesson updated.");
      setLessonDialog(null);
      await load();
    } catch (err) {
      toast.error(apiErr(err.response?.data?.detail) || "Save failed");
    } finally {
      setSavingLesson(false);
    }
  };
  const toggleEnabled = async (lesson, value) => {
    // Optimistic, in-place update — avoids the full-screen reload/flicker.
    setData((prev) => ({
      ...prev,
      subjects: (prev.subjects || []).map((s) => ({
        ...s,
        lessons: (s.lessons || []).map((l) =>
          l.id === lesson.id ? { ...l, student_enabled: value } : l
        ),
      })),
    }));
    try {
      await api.patch(`/tuition/library/lessons/${lesson.id}`, { student_enabled: value });
      toast.success(value ? "Lesson enabled for students." : "Lesson hidden from students.");
    } catch (err) {
      toast.error(apiErr(err.response?.data?.detail) || "Could not update access");
      // Revert on failure
      setData((prev) => ({
        ...prev,
        subjects: (prev.subjects || []).map((s) => ({
          ...s,
          lessons: (s.lessons || []).map((l) =>
            l.id === lesson.id ? { ...l, student_enabled: !value } : l
          ),
        })),
      }));
    }
  };

  const Wrap = embedded ? "div" : Layout;
  return (
    <Wrap>
      {/* hidden file picker shared by every lesson row */}
      <input
        ref={fileInputRef}
        type="file"
        accept="application/pdf,.pdf"
        onChange={handleFile}
        className="hidden"
        data-testid="tuition-lib-file-input"
      />
      {/* hidden CSV picker for bulk lesson import */}
      <input
        ref={csvInputRef}
        type="file"
        accept=".csv,text/csv"
        onChange={onLessonCsv}
        className="hidden"
        data-testid="tuition-lib-csv-input"
      />

      <div className="mb-6 flex items-start justify-between flex-wrap gap-4">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] text-slate-400 mb-2">Admin · Knowledge base</div>
          <h1 className="text-3xl font-bold text-slate-900 dark:text-slate-100" style={{ fontFamily: "Outfit" }}>
            Tuition Library
          </h1>
          <p className="text-slate-500 dark:text-slate-400 mt-1 max-w-2xl">
            Upload one PDF per lesson. We extract the text, chunk it (~500 tokens each), and store embeddings so students can ask questions later.
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <Select value={classFilter} onValueChange={setClassFilter}>
            <SelectTrigger className="h-9 rounded-full w-[160px]" data-testid="tuition-lib-class-select">
              <SelectValue placeholder="Class" />
            </SelectTrigger>
            <SelectContent>
              {(classOptions.all_classes || []).map((c) => (
                <SelectItem key={c} value={c} data-testid={`tuition-lib-class-opt-${c}`}>Class {c}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={subjectFilter} onValueChange={setSubjectFilter} disabled={!data.subjects?.length}>
            <SelectTrigger className="h-9 rounded-full w-[200px]" data-testid="tuition-lib-subject-select">
              <SelectValue placeholder="Subject" />
            </SelectTrigger>
            <SelectContent>
              {(data.subjects || []).map((s) => (
                <SelectItem key={s.id} value={s.id} data-testid={`tuition-lib-subject-opt-${s.id}`}>
                  {s.name} <span className="text-xs text-slate-400 ml-1">({s.indexed_count}/{s.total_count})</span>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button variant="outline" onClick={() => load()} disabled={loading} data-testid="tuition-lib-refresh">
            <RefreshCw className={`h-4 w-4 mr-2 ${loading ? "animate-spin" : ""}`} /> Refresh
          </Button>
          {isDevAdmin && (
            <Button variant="outline" onClick={reprocessVideos} disabled={reprocessing} data-testid="tuition-lib-reprocess-videos" title="Convert & optimise all lesson videos to a browser-friendly format (H.264 + faststart). Fixes videos that won't play or stall.">
              {reprocessing ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <FileUp className="h-4 w-4 mr-2" />} Fix videos
            </Button>
          )}
          <Button variant="outline" onClick={checkTextHealth} disabled={healthLoading} data-testid="tuition-lib-text-health" title="Check whether language-subject PDFs (Kannada, Hindi, …) have proper Unicode text. Flags garbled legacy-font PDFs that make AI question generation drift out of the language.">
            {healthLoading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <FileSearch className="h-4 w-4 mr-2" />} Check text health
          </Button>
          <div className="flex items-center gap-2 px-3 h-9 rounded-full border border-slate-200 dark:border-slate-700" data-testid="autoretry-wrap" title="Automatically re-index PDFs that failed (e.g. once OpenAI credits are topped up)">
            <span className="text-xs font-medium text-slate-600 dark:text-slate-300">Auto-retry failed</span>
            <Switch checked={autoRetry} onCheckedChange={toggleAutoRetry} data-testid="autoretry-toggle" />
          </div>
          <Button variant="outline" onClick={downloadLessonTemplate} data-testid="tuition-lib-csv-template">
            <FileDown className="h-4 w-4 mr-2" /> Template
          </Button>
          <Button
            onClick={() => csvInputRef.current?.click()}
            disabled={importingLessons}
            className="bg-violet-500 hover:bg-violet-600"
            data-testid="tuition-lib-import-lessons-btn"
          >
            {importingLessons ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <FileUp className="h-4 w-4 mr-2" />}
            {importingLessons ? "Importing…" : "Import lessons"}
          </Button>
        </div>
      </div>

      {lessonImportErrors.length > 0 && (
        <div className="mb-4 flex items-center gap-3 rounded-xl border border-rose-200 dark:border-rose-900 bg-rose-50 dark:bg-rose-950/40 px-4 py-3" data-testid="lesson-import-failed-banner">
          <AlertTriangle className="h-4 w-4 text-rose-500 shrink-0" />
          <div className="flex-1 text-sm text-rose-700 dark:text-rose-300">
            <span className="font-semibold">{lessonImportErrors.length}</span> row{lessonImportErrors.length === 1 ? "" : "s"} from your last import couldn't be added. Download the errors, fix them, and re-upload.
          </div>
          <Button size="sm" variant="outline" onClick={downloadLessonImportErrors} className="border-rose-300 text-rose-700 hover:bg-rose-100 shrink-0" data-testid="lesson-import-download-errors">
            <FileDown className="h-4 w-4 mr-1" /> Download errors
          </Button>
          <button type="button" onClick={() => setLessonImportErrors([])} className="text-rose-400 hover:text-rose-600 text-lg leading-none px-1" data-testid="lesson-import-dismiss">×</button>
        </div>
      )}

      <div className="rounded-2xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 p-5 mb-5" data-testid="tuition-lib-summary">
        <div className="flex items-end justify-between flex-wrap gap-3">
          <div>
            <div className="text-xs uppercase tracking-[0.15em] text-slate-400">
              {activeSubject ? `Indexed in ${activeSubject.name}` : "Indexed lessons"}
            </div>
            <div className="text-2xl font-semibold text-slate-900 dark:text-slate-100 mt-0.5">
              <span data-testid="tuition-lib-indexed-count">{totals.indexed}</span>
              <span className="text-slate-400 text-base font-normal"> / {totals.lessons}</span>
            </div>
          </div>
          <div className="text-sm text-slate-500">{pct}% coverage</div>
        </div>
        <Progress value={pct} className="mt-3 h-2" />
      </div>

      {loading ? (
        <div className="text-slate-500">Loading library…</div>
      ) : data.subjects.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-slate-200 dark:border-slate-700 p-10 text-center text-slate-500" data-testid="tuition-lib-empty">
          No subjects configured for Class {classFilter} yet.
        </div>
      ) : !activeSubject ? (
        <div className="text-slate-500">Pick a subject to view lessons.</div>
      ) : (
        <div
          className="border border-slate-200 dark:border-slate-700 rounded-2xl bg-white dark:bg-slate-800 px-5"
          data-testid={`tuition-lib-subject-${activeSubject.id}`}
        >
          <div className="flex items-center justify-between py-4 border-b border-slate-100 dark:border-slate-700">
            <div className="flex items-center gap-3">
              <div className="h-9 w-9 rounded-xl bg-sky-50 dark:bg-sky-900/40 text-sky-700 dark:text-sky-300 flex items-center justify-center">
                <Library className="h-4 w-4" />
              </div>
              <div>
                <div className="text-base font-semibold text-slate-900 dark:text-slate-100">{activeSubject.name}</div>
                <div className="text-xs text-slate-500">{activeSubject.indexed_count} of {activeSubject.total_count} lesson(s) indexed</div>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <div className="text-xs font-medium px-2 py-1 rounded-full bg-slate-100 dark:bg-slate-700 text-slate-600 dark:text-slate-300">
                {activeSubject.total_count ? Math.round((activeSubject.indexed_count / activeSubject.total_count) * 100) : 0}%
              </div>
            </div>
          </div>
          <ul className="divide-y divide-slate-100 dark:divide-slate-700" data-testid="tuition-lib-list">
            {activeSubject.lessons.length === 0 ? (
              <li className="py-8 text-center text-sm text-slate-500" data-testid="tuition-lib-empty-lessons">
                No lessons yet in {activeSubject.name}. Use “Import lessons” to add them.
              </li>
            ) : (
              activeSubject.lessons.map((l) => (
                <LessonRow
                  key={l.id}
                  lesson={l}
                  job={jobsByLesson[l.id]}
                  flagged={healthMap[l.id] || null}
                  onUpload={() => openPicker(l)}
                  onEditLesson={() => openEdit(l)}
                  onToggleEnabled={(val) => toggleEnabled(l, val)}
                  onCancel={() => cancelJob(l)}
                  onRemovePdf={() => removePdf(l)}
                />
              ))
            )}
          </ul>
        </div>
      )}

      {/* Edit lesson dialog */}
      <Dialog open={!!healthResult} onOpenChange={(o) => { if (!o) setHealthResult(null); }}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="tuition-lib-health-dialog">
          <DialogHeader>
            <DialogTitle>Lesson text health (language subjects)</DialogTitle>
            <DialogDescription>
              Checks Kannada / Hindi / other language-subject PDFs for proper Unicode text. Garbled
              (legacy-font) PDFs make AI question generation drift out of the language — re-upload those.
            </DialogDescription>
          </DialogHeader>
          {healthResult && (
            <div className="space-y-4">
              <div className="flex gap-4 text-sm">
                <span className="text-slate-500">Checked: <b className="text-slate-800 dark:text-slate-100">{healthResult.checked}</b></span>
                <span className="text-emerald-600">Clean: <b>{healthResult.ok}</b></span>
                <span className="text-rose-600">Needs re-upload: <b>{healthResult.flagged_count}</b></span>
              </div>
              {healthResult.flagged_count === 0 ? (
                <div className="flex items-center gap-2 text-sm text-emerald-600 rounded-lg border border-emerald-200 dark:border-emerald-800 bg-emerald-50/60 dark:bg-emerald-900/20 p-3">
                  <CheckCircle2 className="h-4 w-4" /> All language-subject lessons have clean, in-language text.
                </div>
              ) : (
                <div className="rounded-lg border border-rose-200 dark:border-rose-800 divide-y divide-rose-100 dark:divide-rose-900/40" data-testid="tuition-lib-health-flagged">
                  {healthResult.flagged.map((f) => (
                    <div key={f.lesson_id} className="p-3 flex items-start gap-2" data-testid={`health-flagged-${f.lesson_id}`}>
                      <AlertTriangle className="h-4 w-4 text-rose-500 mt-0.5 shrink-0" />
                      <div className="min-w-0 text-sm">
                        <div className="font-medium text-slate-800 dark:text-slate-100 truncate">
                          {f.title} <span className="text-slate-400 font-normal">· Class {f.class_name} · {f.subject}</span>
                        </div>
                        <div className="text-xs text-rose-600 mt-0.5">{f.reason}</div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
              {healthResult.flagged_count > 0 && (
                <p className="text-xs text-slate-500">
                  Re-upload each flagged PDF (a copy-selectable, Unicode-Kannada export — not a scanned image or an old
                  ASCII-font file). Then re-index it and regenerate the question paper.
                </p>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={!!lessonDialog} onOpenChange={(o) => { if (!o) setLessonDialog(null); }}>
        <DialogContent data-testid="tuition-lib-lesson-dialog">
          <DialogHeader>
            <DialogTitle>Edit lesson</DialogTitle>
            <DialogDescription>Rename the lesson or change its order in the list.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <div className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1">Title</div>
              <Input
                value={lessonTitle}
                onChange={(e) => setLessonTitle(e.target.value)}
                placeholder="e.g., Photosynthesis Basics"
                data-testid="tuition-lib-lesson-title-input"
                autoFocus
              />
            </div>
            <div>
              <div className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1">Order <span className="font-normal text-slate-400">(optional · lower numbers appear first)</span></div>
              <Input
                type="number"
                min={0}
                value={lessonOrder}
                onChange={(e) => setLessonOrder(e.target.value)}
                placeholder="Auto"
                data-testid="tuition-lib-lesson-order-input"
              />
            </div>
            <div>
              <div className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1">Question videos <span className="font-normal text-slate-400">· students pick a question (or ask aloud) and its YouTube video plays</span></div>
              {lessonDialog && (
                <QAVideoManager
                  lessonId={lessonDialog.lesson.id}
                  lessonTitle={lessonDialog.lesson.title}
                  count={(lessonDialog.lesson.video_qa || []).length}
                  onChange={load}
                />
              )}
            </div>
            {isDevAdmin && (
            <div>
              <div className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1">Video link <span className="font-normal text-slate-400">(optional · YouTube, Vimeo, Google Drive or a direct .mp4 link)</span></div>
              <Input
                value={lessonVideo}
                onChange={(e) => setLessonVideo(e.target.value)}
                placeholder="e.g. https://youtu.be/xxxxxxxxxxx"
                data-testid="tuition-lib-lesson-video-input"
              />
              <div className="text-xs text-slate-400 mt-1">Plays inline in the AI Tutor via "Show me with a short video". An uploaded video (below) takes priority over this link.</div>
            </div>
            )}
            {isDevAdmin && (
              <div className="rounded-lg border border-indigo-200 dark:border-indigo-800 bg-indigo-50/60 dark:bg-indigo-900/20 p-3">
                <div className="text-xs font-semibold text-indigo-600 dark:text-indigo-300 uppercase tracking-wider mb-1 flex items-center gap-1.5">
                  <FileUp className="h-3.5 w-3.5" /> Upload video <span className="font-normal text-indigo-400 normal-case tracking-normal">· Dev Admin · streams in-app, not downloadable</span>
                </div>
                <input
                  ref={videoInputRef}
                  type="file"
                  accept="video/mp4,video/webm,video/ogg,video/quicktime,video/x-matroska,.mp4,.webm,.mov,.mkv,.ogv"
                  className="hidden"
                  onChange={onPickVideo}
                  data-testid="tuition-lib-video-file-input"
                />
                {videoUploadPct !== null ? (
                  <div className="space-y-1.5" data-testid="tuition-lib-video-progress">
                    <Progress value={videoUploadPct} />
                    <div className="text-xs text-slate-500">Uploading… {videoUploadPct}%</div>
                  </div>
                ) : videoInfo ? (
                  <div className="flex items-center justify-between gap-2">
                    <div className="flex items-center gap-2 text-sm text-slate-700 dark:text-slate-200 min-w-0">
                      <CheckCircle2 className="h-4 w-4 text-emerald-500 shrink-0" />
                      <span className="truncate">{videoInfo.video_filename || "Uploaded video"}</span>
                      <span className="text-xs text-slate-400 shrink-0">{fmtBytes(videoInfo.video_size)}</span>
                    </div>
                    <div className="flex items-center gap-1.5 shrink-0">
                      <Button size="sm" variant="outline" onClick={() => videoInputRef.current?.click()} data-testid="tuition-lib-video-replace">
                        <RefreshCw className="h-3.5 w-3.5 mr-1" /> Replace
                      </Button>
                      <Button size="sm" variant="outline" className="text-rose-600 border-rose-200 hover:bg-rose-50" onClick={removeVideo} disabled={removingVideo} data-testid="tuition-lib-video-remove">
                        {removingVideo ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Trash2 className="h-3.5 w-3.5" />}
                      </Button>
                    </div>
                  </div>
                ) : (
                  <Button size="sm" variant="outline" onClick={() => videoInputRef.current?.click()} data-testid="tuition-lib-video-upload-btn">
                    <Upload className="h-3.5 w-3.5 mr-1.5" /> Choose video file
                  </Button>
                )}
              </div>
            )}
            {/* Audio Podcast uploader — same permission as video (Dev Admin) */}
            {isDevAdmin && (
              <div className="rounded-lg border border-violet-200 dark:border-violet-800 bg-violet-50/60 dark:bg-violet-900/20 p-3">
                <div className="text-xs font-semibold text-violet-600 dark:text-violet-300 uppercase tracking-wider mb-1 flex items-center gap-1.5">
                  <FileUp className="h-3.5 w-3.5" /> Audio podcast <span className="font-normal text-violet-400 normal-case tracking-normal">· MP3 / M4A / WAV · streams in-app, not downloadable</span>
                </div>
                <input
                  ref={audioInputRef}
                  type="file"
                  accept="audio/mpeg,audio/mp3,audio/mp4,audio/aac,audio/x-m4a,audio/wav,audio/x-wav,audio/ogg,audio/webm,.mp3,.m4a,.aac,.wav,.ogg,.webm"
                  className="hidden"
                  onChange={onPickAudio}
                  data-testid="tuition-lib-audio-file-input"
                />
                {audioUploadPct !== null ? (
                  <div className="space-y-1.5" data-testid="tuition-lib-audio-progress">
                    <Progress value={audioUploadPct} />
                    <div className="text-xs text-slate-500">Uploading… {audioUploadPct}%</div>
                  </div>
                ) : audioInfo ? (
                  <div className="flex items-center justify-between gap-2">
                    <div className="flex items-center gap-2 text-sm text-slate-700 dark:text-slate-200 min-w-0">
                      <CheckCircle2 className="h-4 w-4 text-emerald-500 shrink-0" />
                      <span className="truncate">{audioInfo.audio_filename || "Uploaded audio"}</span>
                      <span className="text-xs text-slate-400 shrink-0">{fmtBytes(audioInfo.audio_size)}</span>
                    </div>
                    <div className="flex items-center gap-1.5 shrink-0">
                      <Button size="sm" variant="outline" onClick={() => audioInputRef.current?.click()} data-testid="tuition-lib-audio-replace">
                        <RefreshCw className="h-3.5 w-3.5 mr-1" /> Replace
                      </Button>
                      <Button size="sm" variant="outline" className="text-rose-600 border-rose-200 hover:bg-rose-50" onClick={removeAudio} disabled={removingAudio} data-testid="tuition-lib-audio-remove">
                        {removingAudio ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Trash2 className="h-3.5 w-3.5" />}
                      </Button>
                    </div>
                  </div>
                ) : (
                  <Button size="sm" variant="outline" onClick={() => audioInputRef.current?.click()} data-testid="tuition-lib-audio-upload-btn">
                    <Upload className="h-3.5 w-3.5 mr-1.5" /> Choose audio file
                  </Button>
                )}
                <div className="text-[11px] text-slate-500 mt-1.5">Every student who can open this lesson will see the "Audio Podcast" button in the AI Tutor.</div>
              </div>
            )}
            {/* ── MBA (Medium-Bank Assessment) PDF — Dev-Admin only ──
                Uploaded here; consumed as the STRICT source for MEDIUM
                question papers (mirrors LBA→Easy). If missing, Medium
                generation is blocked with a clear message. */}
            {isDevAdmin && (
              <div className="rounded-lg border border-amber-200 dark:border-amber-800 bg-amber-50/60 dark:bg-amber-900/20 p-3">
                <div className="text-xs font-semibold text-amber-700 dark:text-amber-300 uppercase tracking-wider mb-1 flex items-center gap-1.5">
                  <FileUp className="h-3.5 w-3.5" /> Medium Question Bank (MBA)
                  <span className="font-normal text-amber-500 normal-case tracking-normal">· Dev Admin · PDF · source for Medium-level papers</span>
                </div>
                <input
                  ref={mbaInputRef}
                  type="file"
                  accept="application/pdf,.pdf"
                  className="hidden"
                  onChange={onPickMba}
                  data-testid="tuition-lib-mba-file-input"
                />
                {mbaInfo ? (
                  <div className="flex items-center justify-between gap-2">
                    <div className="flex items-center gap-2 text-sm text-slate-700 dark:text-slate-200 min-w-0">
                      <CheckCircle2 className="h-4 w-4 text-emerald-500 shrink-0" />
                      <span className="truncate">{mbaInfo.mba_filename || "Uploaded question bank"}</span>
                      <span className="text-xs text-slate-400 shrink-0">{fmtBytes(mbaInfo.mba_size)}</span>
                      <span className="text-[11px] text-slate-400 shrink-0 hidden sm:inline">· {fmtWhen(mbaInfo.mba_uploaded_at)}</span>
                    </div>
                    <div className="flex items-center gap-1.5 shrink-0">
                      <Button size="sm" variant="outline" onClick={previewMba} data-testid="tuition-lib-mba-preview">
                        <FileSearch className="h-3.5 w-3.5 mr-1" /> Preview
                      </Button>
                      <Button size="sm" variant="outline" onClick={() => mbaInputRef.current?.click()} disabled={mbaBusy} data-testid="tuition-lib-mba-replace">
                        {mbaBusy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <><RefreshCw className="h-3.5 w-3.5 mr-1" /> Replace</>}
                      </Button>
                      <Button size="sm" variant="outline" className="text-rose-600 border-rose-200 hover:bg-rose-50" onClick={removeMba} disabled={mbaBusy} data-testid="tuition-lib-mba-remove">
                        {mbaBusy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Trash2 className="h-3.5 w-3.5" />}
                      </Button>
                    </div>
                  </div>
                ) : (
                  <Button size="sm" variant="outline" onClick={() => mbaInputRef.current?.click()} disabled={mbaBusy} data-testid="tuition-lib-mba-upload-btn">
                    {mbaBusy ? <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" /> : <Upload className="h-3.5 w-3.5 mr-1.5" />} Choose Medium-bank PDF
                  </Button>
                )}
                <div className="text-[11px] text-slate-500 mt-1.5">
                  Teachers who pick <strong>Medium</strong> in "Create a question paper" will get questions strictly from this bank (verbatim). If no bank is uploaded for a selected lesson, Medium generation is blocked with a clear message.
                </div>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setLessonDialog(null)} disabled={savingLesson}>Cancel</Button>
            <Button
              className="bg-sky-500 hover:bg-sky-600"
              onClick={saveLesson}
              disabled={savingLesson || !lessonTitle.trim()}
              data-testid="tuition-lib-lesson-save"
            >
              {savingLesson ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Pencil className="h-4 w-4 mr-1" />}
              Save changes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Wrap>
  );
}

function LessonRow({ lesson, job, flagged, onUpload, onEditLesson, onToggleEnabled, onCancel, onRemovePdf }) {
  const indexed = !!lesson.indexed;
  const busy = !!job && job.status === "processing";
  const pct = jobPct(job);
  const enabled = lesson.student_enabled !== false;
  const [confirmOpen, setConfirmOpen] = useState(false);
  const downloadHref = useMemo(() => {
    if (!indexed) return null;
    const base = process.env.REACT_APP_BACKEND_URL || "";
    return `${base}/api/tuition/library/${lesson.id}/download`;
  }, [indexed, lesson.id]);

  return (
    <li className="py-3 flex items-center justify-between gap-3 flex-wrap" data-testid={`tuition-lib-lesson-${lesson.id}`}>
      <div className="flex items-start gap-3 min-w-0 flex-1">
        <div className={`mt-0.5 h-6 w-6 rounded-full flex items-center justify-center shrink-0 ${indexed ? "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300" : "bg-slate-100 text-slate-400 dark:bg-slate-700"}`}>
          {indexed ? <CheckCircle2 className="h-4 w-4" /> : <CircleDashed className="h-4 w-4" />}
        </div>
        <div className="min-w-0">
          <div className="text-sm font-medium text-slate-900 dark:text-slate-100 truncate flex items-center gap-1.5">
            <span className="truncate">{lesson.title}</span>
            {lesson.order != null && (
              <span className="text-[10px] font-mono text-slate-400 shrink-0">#{lesson.order}</span>
            )}
            {flagged && !flagged.ok && (
              <span
                className="inline-flex items-center gap-1 text-[10px] font-semibold px-1.5 py-0.5 rounded-full bg-rose-100 text-rose-700 dark:bg-rose-900/40 dark:text-rose-300 shrink-0"
                title={`Only ${Math.round((flagged.script_ratio || 0) * 100)}% ${flagged.language} script — likely a garbled/legacy-font PDF. Re-upload a clean Unicode PDF.`}
                data-testid={`tuition-lib-garbled-${lesson.id}`}
              >
                <AlertTriangle className="h-3 w-3" /> Garbled text
              </span>
            )}
            {flagged && flagged.ok && (
              <span
                className="inline-flex items-center gap-1 text-[10px] font-semibold px-1.5 py-0.5 rounded-full bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300 shrink-0"
                title={`${Math.round((flagged.script_ratio || 0) * 100)}% ${flagged.language} Unicode — good for AI generation.`}
                data-testid={`tuition-lib-langok-${lesson.id}`}
              >
                <CheckCircle2 className="h-3 w-3" /> {Math.round((flagged.script_ratio || 0) * 100)}% {flagged.language}
              </span>
            )}
          </div>
          <div className="text-xs text-slate-500 dark:text-slate-400 mt-0.5 flex items-center gap-2 flex-wrap">
            {indexed ? (
              <>
                <span className="inline-flex items-center gap-1"><FileText className="h-3 w-3" /> {lesson.filename}</span>
                <span>·</span>
                <span>{lesson.chunk_count} chunk{lesson.chunk_count === 1 ? "" : "s"}</span>
                <span>·</span>
                <span>{lesson.page_count} page{lesson.page_count === 1 ? "" : "s"}</span>
                <span>·</span>
                <span>{fmtBytes(lesson.file_size)}</span>
                <span>·</span>
                <span>indexed {fmtWhen(lesson.indexed_at)}</span>
              </>
            ) : (
              <span>Not indexed yet</span>
            )}
          </div>
          {busy && (
            <div className="mt-2" data-testid={`tuition-lib-job-${lesson.id}`}>
              <div className="flex items-center justify-between text-[11px] text-sky-600 dark:text-sky-400 mb-1">
                <span>{jobLabel(job)}</span>
                <span>{pct}%</span>
              </div>
              <Progress value={pct} className="h-1.5" data-testid={`tuition-lib-progress-${lesson.id}`} />
              <button
                type="button"
                onClick={onCancel}
                className="mt-1.5 inline-flex items-center gap-1 text-[11px] font-semibold text-rose-600 hover:text-rose-700"
                data-testid={`tuition-lib-stop-${lesson.id}`}
                title="Stop indexing this PDF"
              >
                <Ban className="h-3 w-3" /> Stop indexing
              </button>
            </div>
          )}
        </div>
      </div>

      <div className="flex items-center gap-3 shrink-0">
        {indexed && flagged && !flagged.ok && !busy && (
          <Button
            size="sm"
            className="rounded-full bg-amber-500 hover:bg-amber-600 text-white"
            onClick={onUpload}
            title="Garbled/legacy-font PDF. Re-upload a clean Unicode PDF to fix AI generation."
            data-testid={`tuition-lib-reupload-${lesson.id}`}
          >
            <Upload className="h-3.5 w-3.5 mr-1" /> Re-upload
          </Button>
        )}
        {indexed ? (
          <a href={downloadHref} target="_blank" rel="noreferrer" data-testid={`tuition-lib-view-${lesson.id}`}>
            <Button variant="outline" size="sm" className="rounded-full">
              <FileSearch className="h-3.5 w-3.5 mr-1" /> View
            </Button>
          </a>
        ) : (
          <Button
            size="sm"
            className="rounded-full bg-sky-500 hover:bg-sky-600"
            onClick={onUpload}
            disabled={busy}
            data-testid={`tuition-lib-upload-${lesson.id}`}
          >
            {busy ? <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" /> : <Upload className="h-3.5 w-3.5 mr-1" />}
            {busy ? "Indexing…" : "Upload PDF"}
          </Button>
        )}
        {indexed && !busy && (
          <Button
            variant="ghost"
            size="sm"
            className="rounded-full text-rose-500 hover:text-rose-700 hover:bg-rose-50 dark:hover:bg-rose-900/30"
            onClick={() => setConfirmOpen(true)}
            title="Remove the uploaded PDF"
            data-testid={`tuition-lib-remove-pdf-${lesson.id}`}
          >
            <Trash2 className="h-3.5 w-3.5" />
          </Button>
        )}
        <Button
          variant="ghost"
          size="sm"
          className="rounded-full text-slate-500 hover:text-slate-900 dark:hover:text-slate-100"
          onClick={onEditLesson}
          disabled={busy}
          title="Rename / reorder lesson"
          data-testid={`tuition-lib-edit-lesson-${lesson.id}`}
        >
          <Pencil className="h-3.5 w-3.5" />
        </Button>
        {/* Student access toggle */}
        <div className="flex items-center gap-2 pl-1 border-l border-slate-200 dark:border-slate-700">
          <span className={`text-xs font-medium ${enabled ? "text-emerald-600 dark:text-emerald-400" : "text-slate-400"}`}>
            {enabled ? "Visible" : "Hidden"}
          </span>
          <Switch
            checked={enabled}
            onCheckedChange={(v) => onToggleEnabled(v)}
            disabled={busy}
            title={enabled ? "Students can access this lesson" : "Hidden from students"}
            data-testid={`tuition-lib-toggle-${lesson.id}`}
          />
        </div>
      </div>

      <AlertDialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <AlertDialogContent data-testid={`tuition-lib-remove-confirm-${lesson.id}`}>
          <AlertDialogHeader>
            <AlertDialogTitle>Remove this PDF?</AlertDialogTitle>
            <AlertDialogDescription>
              This deletes the uploaded PDF and its indexed text for “{lesson.title}”. The lesson stays,
              and you can upload a fresh PDF anytime. Students won’t be able to access it until you do.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid={`tuition-lib-remove-cancel-${lesson.id}`}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => { setConfirmOpen(false); onRemovePdf(); }}
              className="bg-rose-600 hover:bg-rose-700"
              data-testid={`tuition-lib-remove-confirm-btn-${lesson.id}`}
            >
              Remove PDF
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </li>
  );
}
